import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { BingSitesDialog } from '@/components/bing-sites-dialog'
import { EngineMark } from '@/components/engine-mark'
import { PageHeader } from '@/components/page-header'
import { Panel } from '@/components/panel'
import { PropertyPickerDialog } from '@/components/property-picker-dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate } from '@/lib/format'
import {
  type BingConnection,
  bingConnectionsQuery,
  type Connection,
  connectionsQuery,
  useAddBingKey,
  useDisconnectGoogle,
  useRemoveBingKey,
  useStartGoogleConnect,
} from '@/lib/queries'

export const Route = createFileRoute('/_app/$org/settings/connections')({
  // The OAuth callback lands back here with the connection it just created, so
  // the property picker can open straight onto it.
  validateSearch: (search: Record<string, unknown>): { connection?: string } =>
    typeof search.connection === 'string' ? { connection: search.connection } : {},
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(connectionsQuery),
      context.queryClient.ensureQueryData(bingConnectionsQuery),
    ]),
  component: ConnectionsSettingsPage,
})

function ConnectionsSettingsPage() {
  const { org } = Route.useParams()
  const { connection: fromCallback } = Route.useSearch()
  const navigate = useNavigate()
  const { data, isLoading } = useQuery(connectionsQuery)
  const startConnect = useStartGoogleConnect()
  const [picking, setPicking] = useState<Connection | null>(null)

  const connections = data?.connections ?? []

  // Arriving straight from Google: open the picker on the account just added and
  // drop the parameter so a refresh does not reopen it.
  useEffect(() => {
    if (!fromCallback) return
    const match = connections.find((candidate) => candidate.id === fromCallback)
    if (!match) return
    setPicking(match)
    navigate({ to: '/$org/settings/connections', params: { org }, replace: true })
  }, [fromCallback, connections, navigate, org])

  async function connect() {
    try {
      await startConnect.mutateAsync()
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : 'Could not start the Google connection')
    }
  }

  return (
    <>
      <PageHeader
        title="Connections"
        description="The search engine accounts this workspace reads its search data through."
      />

      <Panel
        title={
          <span className="flex items-center gap-2">
            <EngineMark engine="google" />
            Google
          </span>
        }
        meta={connections.length > 0 ? `${connections.length}` : undefined}
        actions={
          connections.length > 0 ? (
            <Button size="sm" variant="outline" onClick={connect} disabled={startConnect.isPending}>
              {startConnect.isPending ? 'Redirecting…' : 'Add account'}
            </Button>
          ) : undefined
        }
        flush={connections.length > 0}
      >
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : connections.length === 0 ? (
          <div className="space-y-4">
            <p className="text-[13px] text-muted-foreground">
              Connecting a Google account lets SEO Plum read the Search Console properties it owns,
              and link them to sites in this workspace. Add one account per login — an agency
              holding a separate login per client can connect all of them here.
            </p>
            <Button size="sm" onClick={connect} disabled={startConnect.isPending}>
              {startConnect.isPending ? 'Redirecting…' : 'Connect Google'}
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {connections.map((connection) => (
              <ConnectionRow
                key={connection.id}
                connection={connection}
                onPick={() => setPicking(connection)}
                onReconnect={connect}
              />
            ))}
          </ul>
        )}
      </Panel>

      <BingPanel />

      {picking && (
        <PropertyPickerDialog
          connectionId={picking.id}
          email={picking.googleEmail}
          syncedAt={picking.propertiesSyncedAt}
          open
          onOpenChange={(open) => {
            if (!open) setPicking(null)
          }}
        />
      )}
    </>
  )
}

function ConnectionRow({
  connection,
  onPick,
  onReconnect,
}: {
  connection: Connection
  onPick: () => void
  onReconnect: () => void
}) {
  const disconnect = useDisconnectGoogle()
  const [confirming, setConfirming] = useState(false)
  const invalid = Boolean(connection.invalidatedAt)

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5">
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span
            className={`size-1.5 shrink-0 rounded-full ${invalid ? 'bg-warning' : 'bg-success'}`}
            aria-hidden
          />
          <span className="truncate font-mono text-[13px]">{connection.googleEmail}</span>
        </span>
        <span className="block pt-1 pl-3.5 text-[12px] text-muted-foreground">
          {invalid
            ? // Testing-mode refresh tokens expire after seven days, so this is
              // the state most connections land in during development.
              (connection.invalidatedReason ??
              'Google access expired. Reconnect to resume syncing.')
            : `${connection.propertyCount} propert${connection.propertyCount === 1 ? 'y' : 'ies'} · ${connection.siteCount} site${connection.siteCount === 1 ? '' : 's'}${
                connection.propertiesSyncedAt
                  ? ` · checked ${formatDate(connection.propertiesSyncedAt)}`
                  : ''
              }`}
        </span>
      </span>

      <span className="flex shrink-0 items-center gap-2">
        {invalid ? (
          <Button size="sm" variant="outline" onClick={onReconnect}>
            Reconnect
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={onPick}>
            Choose sites
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
          Disconnect
        </Button>
      </span>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {connection.googleEmail}?</AlertDialogTitle>
            <AlertDialogDescription>
              Sites stay, and so does everything already synced — they just stop receiving new
              Search Console data until an account is connected again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await disconnect.mutateAsync(connection.id)
                  toast.success(`Disconnected ${connection.googleEmail}`)
                } catch (cause) {
                  toast.error(cause instanceof Error ? cause.message : 'Could not disconnect')
                }
              }}
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  )
}

/**
 * Bing takes a key rather than a sign-in, and needs no picker: its sites link
 * to this workspace's by domain, so pasting the key is the whole setup.
 */
function BingPanel() {
  const { data, isLoading } = useQuery(bingConnectionsQuery)
  const addKey = useAddBingKey()
  const [apiKey, setApiKey] = useState('')
  const connections = data?.connections ?? []

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    try {
      await addKey.mutateAsync(apiKey.trim())
      setApiKey('')
      toast.success('Bing connected')
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : 'Could not add that key')
    }
  }

  const form = (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
      <Input
        value={apiKey}
        onChange={(event) => setApiKey(event.target.value)}
        placeholder="Bing Webmaster API key"
        className="max-w-sm font-mono text-[13px]"
        autoComplete="off"
        spellCheck={false}
      />
      <Button size="sm" type="submit" disabled={addKey.isPending || apiKey.trim().length === 0}>
        {addKey.isPending ? 'Checking…' : 'Add key'}
      </Button>
    </form>
  )

  return (
    <Panel
      title={
        <span className="flex items-center gap-2">
          <EngineMark engine="bing" />
          Bing
        </span>
      }
      meta={connections.length > 0 ? `${connections.length}` : undefined}
      flush={connections.length > 0}
    >
      {isLoading ? (
        <Skeleton className="h-10" />
      ) : connections.length === 0 ? (
        <div className="space-y-4">
          <p className="text-[13px] text-muted-foreground">
            In Bing Webmaster Tools, open Settings, then API access, and generate a key. Every site
            it can read is matched to this workspace by domain.
          </p>
          {form}
        </div>
      ) : (
        <>
          <ul className="divide-y divide-border">
            {connections.map((connection) => (
              <BingRow key={connection.id} connection={connection} />
            ))}
          </ul>
          <div className="border-t border-border px-4 py-3.5">{form}</div>
        </>
      )}
    </Panel>
  )
}

function BingRow({ connection }: { connection: BingConnection }) {
  const remove = useRemoveBingKey()
  const [confirming, setConfirming] = useState(false)
  const [viewing, setViewing] = useState(false)
  const invalid = Boolean(connection.invalidatedAt)

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5">
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span
            className={`size-1.5 shrink-0 rounded-full ${invalid ? 'bg-warning' : 'bg-success'}`}
            aria-hidden
          />
          <span className="truncate font-mono text-[13px]">key ····{connection.keyHint}</span>
        </span>
        <span className="block pt-1 pl-3.5 text-[12px] text-muted-foreground">
          {invalid
            ? 'Bing no longer accepts this key. Add a new one.'
            : `${connection.siteCount} site${connection.siteCount === 1 ? '' : 's'} · ${connection.linkedCount} linked${
                connection.sitesSyncedAt ? ` · checked ${formatDate(connection.sitesSyncedAt)}` : ''
              }`}
        </span>
      </span>

      <span className="flex shrink-0 items-center gap-2">
        {!invalid && (
          <Button size="sm" variant="outline" onClick={() => setViewing(true)}>
            Sites
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
          Remove
        </Button>
      </span>

      {viewing && (
        <BingSitesDialog
          connectionId={connection.id}
          keyHint={connection.keyHint}
          open
          onOpenChange={setViewing}
        />
      )}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this Bing key?</AlertDialogTitle>
            <AlertDialogDescription>
              Its Bing figures are removed from every site it was linked to. Adding the key again
              reads them back in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await remove.mutateAsync(connection.id)
                  toast.success('Bing key removed')
                } catch (cause) {
                  toast.error(cause instanceof Error ? cause.message : 'Could not remove the key')
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  )
}
